import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, map, switchMap, of, from } from 'rxjs';
import { Auth, authState } from '@angular/fire/auth';
import { TwoFactorService } from './services/two-factor.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthorizeGuard  {

  auth = inject(Auth);
  router = inject(Router);
  twoFactorService = inject(TwoFactorService);
    
  constructor() {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    // Allow access to setup and verify routes during 2FA flow
    if (state.url === '/setup-2fa' || state.url === '/verify-otp' || state.url.startsWith('/login')) {
      return authState(this.auth).pipe(
        map((user) => {
          if (user) {
            return true;
          } else if (state.url.startsWith('/login')) {
            return true; // Allow login page
          } else {
            return this.router.createUrlTree(['login']);
          }
        })
      );
    }

    // For all other routes, check auth and 2FA status
    return authState(this.auth).pipe(
      switchMap((user) => {
        if (!user) {
          return of(this.router.createUrlTree(['login']));
        }

        // Check if 2FA is enabled (convert promise to observable)
        return from(this.twoFactorService.isTwoFactorEnabled(user.uid)).pipe(
          map((twoFactorEnabled) => {
            if (!twoFactorEnabled) {
              // User is authenticated but 2FA is not set up - redirect to setup
              return this.router.createUrlTree(['/setup-2fa']);
            } else {
              // Check if user has verified 2FA in this session
              const isVerified = this.twoFactorService.isSessionVerified(user.uid);
              if (!isVerified) {
                // User has 2FA enabled but hasn't verified in this session - redirect to verification
                return this.router.createUrlTree(['/verify-otp']);
              }
              // User is authenticated, 2FA is enabled, and verified in this session - allow access
              return true;
            }
          })
        );
      })
    );
  }

  /**
   * Helper method to check 2FA status
   */
  async checkTwoFactorStatus(uid: string): Promise<boolean> {
    return await this.twoFactorService.isTwoFactorEnabled(uid);
  }
}